import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck, Save } from 'lucide-react';
import { Pill, useFetch, runAction } from './shared';

function ProbeResult({ r }) {
  if (!r) return null;
  const c = r.company?.data;
  return (
    <div className="mt-3 border border-slate-200 bg-white p-4 text-sm space-y-2" data-testid={`dian-probe-result-${r.env}`}>
      <div className="flex items-center gap-2">
        <span className="font-bold">{r.env}</span>
        <Pill value={r.auth?.ok ? 'ok' : 'error'} testId={`dian-probe-auth-${r.env}`} />
        <span className="text-xs text-slate-500">{r.auth?.ok ? `token válido ${r.auth.expires_in_seconds}s` : r.auth?.reason}</span>
      </div>
      {r.ranges && (
        <div data-testid={`dian-probe-ranges-${r.env}`}>
          <div className="text-[10px] uppercase font-bold text-slate-500">Rangos ({r.ranges.items.length})</div>
          {r.ranges.items.map((x) => {
            const al = (r.range_alerts?.assessed || []).find((a) => a.id === x.id);
            return (
            <div key={x.id} className="font-mono text-xs" data-testid={`dian-range-${r.env}-${x.id}`}>
              {al && <Pill value={al.level === 'ok' ? 'ok' : al.level === 'critical' ? 'failed' : 'completed_with_errors'} testId={`dian-range-level-${r.env}-${x.id}`} />}{' '}
              id {x.id} · {x.document} · {x.prefix} {x.from}–{x.to} (actual {x.current}) · res. {x.resolution_number} · {x.start_date}→{x.end_date} · {x.is_active ? 'activo' : 'inactivo'}{x.is_expired ? ' · vencido' : ''}
              {al && al.level !== 'ok' && <span className="text-rose-700"> · {al.reasons.join(', ')} ({al.pct_left ?? '—'}% · {al.days_left ?? '—'} días)</span>}
            </div>
            );
          })}
        </div>
      )}
      {c && (
        <div className="text-xs" data-testid={`dian-probe-company-${r.env}`}>
          <div className="text-[10px] uppercase font-bold text-slate-500">Emisor en Factus</div>
          NIT {c.nit}-{c.dv} · {c.company} · {c.address} · {c.municipality?.name || c.municipality?.code} ·
          tributo {c.tribute?.code} · org {c.legal_organization?.code} · CIIU {c.economic_activity}
          <div className={r.issuer_nit_matches ? 'text-emerald-700' : 'text-rose-700'}>
            NIT esperado {r.expected_issuer_nit}: {r.issuer_nit_matches ? 'coincide' : 'NO coincide'}
          </div>
        </div>
      )}
    </div>
  );
}

function RangesForm() {
  const { data, load } = useFetch('/admin/dian/settings');
  const [inv, setInv] = useState('');
  const [cn, setCn] = useState('');
  useEffect(() => {
    setInv(data?.factus_numbering_range_id_invoice ?? '');
    setCn(data?.factus_numbering_range_id_credit_note ?? '');
  }, [data]);
  const toNum = (v) => (v === '' || v == null ? null : Number(v));
  const save = () => runAction(() => api.put('/admin/dian/settings', {
    factus_numbering_range_id_invoice: toNum(inv),
    factus_numbering_range_id_credit_note: toNum(cn),
  }), 'Rangos guardados', load);
  return (
    <div className="border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3" data-testid="dian-ranges-form">
      <div>
        <label className="text-[10px] uppercase font-bold text-slate-500">ID rango facturas</label>
        <Input data-testid="dian-range-invoice-input" value={inv} onChange={(e) => setInv(e.target.value)} className="rounded-none w-36 font-mono" placeholder="vacío = único activo" />
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold text-slate-500">ID rango notas crédito</label>
        <Input data-testid="dian-range-cn-input" value={cn} onChange={(e) => setCn(e.target.value)} className="rounded-none w-36 font-mono" placeholder="vacío = único activo" />
      </div>
      <Button className="rounded-none" data-testid="dian-ranges-save-btn" onClick={save}><Save className="h-4 w-4 mr-1" /> Guardar</Button>
    </div>
  );
}

export function DianFactusPanel() {
  const [results, setResults] = useState({});
  const probe = async (env) => {
    const r = await runAction(() => api.post(`/admin/dian/factus/probe?env=${env}`), `Sonda ${env} ejecutada`);
    if (r) setResults((p) => ({ ...p, [env]: r }));
  };
  return (
    <div className="space-y-4" data-testid="dian-factus-panel">
      <div className="border border-slate-200 bg-white p-4">
        <p className="text-sm text-slate-600">Sondas de solo lectura: piden un token, consultan rangos y datos del emisor. Nunca emiten documentos.</p>
        <div className="mt-3 flex gap-2">
          <Button variant="outline" className="rounded-none" data-testid="dian-probe-sandbox-btn" onClick={() => probe('sandbox')}>
            <ShieldCheck className="h-4 w-4 mr-1" /> Probar Sandbox
          </Button>
          <Button variant="outline" className="rounded-none" data-testid="dian-probe-production-btn" onClick={() => probe('production')}>
            <ShieldCheck className="h-4 w-4 mr-1" /> Probar Producción
          </Button>
        </div>
        <ProbeResult r={results.sandbox} />
        <ProbeResult r={results.production} />
      </div>
      <RangesForm />
    </div>
  );
}
